import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { TenantConfig, DomainTenantMap } from '../models/tenant.models';

/**
 * TenantConfigService
 *
 * Manages multi-tenant configuration based on the current domain/subdomain.
 * Each hospital is identified by its domain and has its own API Key and metadata.
 *
 * Architecture:
 * - Development (localhost): Maps to HMRS pre-prod credentials
 * - Production (subdomain): Each subdomain maps to its hospital configuration
 *
 * Example:
 * - localhost:4200 → HMRS (pre-prod)
 * - hmrs.tudominio.com → HMRS (prod)
 * - hospitalb.tudominio.com → Hospital B (prod)
 *
 * This service is initialized once at app startup and provides the tenant
 * configuration to interceptors and services throughout the application.
 */
@Injectable({
  providedIn: 'root',
})
export class TenantConfigService {
  private currentTenant: TenantConfig | null = null;

  /**
   * Domain-to-tenant mapping.
   * Add new hospitals here as they are onboarded.
   *
   * ⚠️  ONLY used when `environment.useTenantMap` is `true`.
   *     When `false`, the values come directly from `environment.ts`.
   */
  private readonly tenantMap: DomainTenantMap = {
    // ─── Development ───────────────────────────────────────────────────────
    localhost: {
      apiKey: '8597919b0e30b4546e27eff9910b249d50ff5862d90989ff647114457ca1c614',
      apiUrl: 'http://localhost:3000/api',
      hospital: {
        id: 'HMRS',
        name: 'Hospital Mental',
        fullName: 'Hospital Mental',
        code: 'HMRS',
        environment: 'pre-prod',
      },
      production: false,
    },

    // ─── Pre-production - HMRS (DAG Comunicaciones) ────────────────────────
    'hmrs-rda-pre-production.dagcomunicaciones.com.co': {
      apiKey: '8597919b0e30b4546e27eff9910b249d50ff5862d90989ff647114457ca1c614',
      apiUrl: 'https://rda-api-pre-production.dagcomunicaciones.com.co/api',
      hospital: {
        id: 'HMRS',
        name: 'Hospital Mental',
        fullName: 'Hospital Mental',
        code: 'HMRS',
        environment: 'pre-prod',
      },
      production: false,
    },

    // ─── Production - HMRS ─────────────────────────────────────────────────
    'hmrs.tudominio.com': {
      apiKey: 'd38ee2e282b46376c6f9e68cd86c48d3fba9ca51266631117652564ae10b0efc',
      apiUrl: 'https://api-rda.hmrs.gov.co/api',
      hospital: {
        id: 'HMRS',
        name: 'Hospital Mental',
        fullName: 'Hospital Mental',
        code: 'HMRS',
        environment: 'production',
      },
      production: true,
    },

    // ─── Production - Hospital Universitario de Bucaramanga (Example) ──────
    // Uncomment and configure when adding this hospital:
    //
    // 'hub.dagcomunicaciones.com.co': {
    //   apiKey: 'HUB_PROD_API_KEY_HERE',
    //   apiUrl: 'https://api-rda-hub.dagcomunicaciones.com.co/api',
    //   hospital: {
    //     id: 'HUB',
    //     name: 'Hospital Universitario de Bucaramanga',
    //     fullName: 'Hospital Universitario de Bucaramanga - E.S.E.',
    //     code: 'HUB',
    //     environment: 'production',
    //   },
    //   production: true,
    // },

    // ─── Production - Clínica Chicamocha (Example) ─────────────────────────
    // Uncomment and configure when adding this hospital:
    //
    // 'clinicachicamocha.dagcomunicaciones.com.co': {
    //   apiKey: 'CCH_PROD_API_KEY_HERE',
    //   apiUrl: 'https://api-rda-cch.dagcomunicaciones.com.co/api',
    //   hospital: {
    //     id: 'CCH',
    //     name: 'Clínica Chicamocha',
    //     fullName: 'Clínica Chicamocha S.A.S.',
    //     code: 'CCH',
    //     environment: 'production',
    //   },
    //   production: true,
    // },
  };

  constructor() {
    this.initializeTenant();
  }

  /**
   * Initializes the tenant configuration.
   *
   * When `environment.useTenantMap` is `true`, detects the hospital
   * automatically by matching the current hostname against `tenantMap`.
   *
   * When `false` (deploy-per-subdomain mode), uses `apiUrl`, `apiKey`
   * and `hospital` directly from the environment file, ignoring the map.
   */
  private initializeTenant(): void {
    if (!environment.useTenantMap) {
      // Deploy-per-subdomain mode: use environment values directly
      this.currentTenant = {
        apiKey: environment.apiKey,
        apiUrl: environment.apiUrl,
        hospital: environment.hospital as any,
        production: environment.production,
      };
      console.log(`[TenantConfigService] useTenantMap=false — using static environment config`);
      console.log(
        `[TenantConfigService] Loaded tenant: ${this.currentTenant!.hospital.name} (${this.currentTenant!.hospital.id})`,
      );
      console.log(`[TenantConfigService] Environment: ${this.currentTenant!.hospital.environment}`);
      return;
    }

    // Multi-tenant mode: detect by hostname
    const hostname = this.getHostname();
    const tenant = this.tenantMap[hostname];

    if (!tenant) {
      console.error(`[TenantConfigService] No tenant configuration found for domain: ${hostname}`);
      console.error('[TenantConfigService] Falling back to localhost configuration');
      this.currentTenant = this.tenantMap['localhost'];
    } else {
      this.currentTenant = tenant;
      console.log(
        `[TenantConfigService] Loaded tenant: ${tenant.hospital.name} (${tenant.hospital.id})`,
      );
      console.log(`[TenantConfigService] Environment: ${tenant.hospital.environment}`);
    }
  }

  /**
   * Returns the current hostname from window.location.
   * Extracted for testability.
   */
  private getHostname(): string {
    return window.location.hostname;
  }

  /**
   * Returns the current tenant configuration.
   * Throws an error if no tenant is configured (should never happen after initialization).
   */
  getTenantConfig(): TenantConfig {
    if (!this.currentTenant) {
      throw new Error('[TenantConfigService] Tenant not initialized. This should never happen.');
    }
    return this.currentTenant;
  }

  /**
   * Returns the API Key for the current tenant.
   * Used by the HTTP interceptor to inject the X-API-Key header.
   */
  getApiKey(): string {
    return this.getTenantConfig().apiKey;
  }

  /**
   * Returns the backend API URL for the current tenant.
   */
  getApiUrl(): string {
    return this.getTenantConfig().apiUrl;
  }

  /**
   * Returns the hospital metadata for the current tenant.
   * Useful for displaying hospital information in the UI.
   */
  getHospitalMetadata() {
    return this.getTenantConfig().hospital;
  }

  /**
   * Returns whether the current tenant is in production mode.
   */
  isProduction(): boolean {
    return this.getTenantConfig().production;
  }

  /**
   * Returns all configured domains (for debugging/admin purposes).
   */
  getConfiguredDomains(): string[] {
    return Object.keys(this.tenantMap);
  }
}
